/**
 * What the phone can and cannot do, and what follows from it.
 *
 * iOS is the constraining platform, so the app is built against its limits and
 * Android inherits a build that is stricter than it needs to be. Three of those
 * limits change what the app must do, not just how it looks:
 *
 *  - **There is no install prompt.** Android fires `beforeinstallprompt` and the
 *    app can offer a button. iOS has no such event: the engineer must tap Share
 *    then "Add to Home Screen", *and only from Safari* — Chrome and the in-app
 *    browsers inside Teams or WhatsApp cannot install anything. So the app has
 *    to tell them, in those words, and know when not to.
 *  - **There is no Background Sync.** Android can hand the browser a job to
 *    finish after the tab closes. On iOS, closing the app stops the upload where
 *    it is. Sync is therefore foreground-only and the app says so rather than
 *    implying evidence is on its way.
 *  - **Storage is evicted.** Safari clears site data for apps it considers
 *    unused, and an installed PWA has a larger but still finite quota. Photos
 *    waiting to upload are the only copy of a day's evidence.
 *
 * All detection is from a supplied environment, never read off `window` inside
 * these functions, so the decisions are testable without a browser.
 */

export interface Env {
  userAgent: string;
  /** `navigator.standalone` — iOS only, true when launched from the home screen. */
  iosStandalone?: boolean | undefined;
  /** Result of `matchMedia('(display-mode: standalone)')` — the standard signal. */
  displayModeStandalone?: boolean | undefined;
  /** Whether `onbeforeinstallprompt` exists: Chromium's install hook. */
  hasInstallPrompt?: boolean | undefined;
  /** Whether the service worker registration exposes `sync`. */
  hasBackgroundSync?: boolean | undefined;
}

export type Browser = 'safari' | 'ios-other' | 'chromium' | 'other';

export interface Platform {
  ios: boolean;
  /** iPadOS 13+ reports itself as a Mac; a touch-capable "Mac" is an iPad. */
  android: boolean;
  browser: Browser;
  installed: boolean;
  /** Whether this browser can install the app at all. */
  canInstall: boolean;
  backgroundSync: boolean;
}

export function detect(env: Env): Platform {
  const ua = env.userAgent;
  const ios = /iPhone|iPad|iPod/i.test(ua) || (/Macintosh/i.test(ua) && /Mobile|Touch/i.test(ua));
  const android = /Android/i.test(ua);

  let browser: Browser;
  if (ios) {
    // Every iOS browser is WebKit underneath, but only Safari can install.
    // CriOS/FxiOS/EdgiOS are the third-party wrappers; a UA carrying "Safari"
    // with none of those markers and no in-app-browser marker is real Safari.
    const wrapper = /CriOS|FxiOS|EdgiOS|OPiOS|GSA\//i.test(ua);
    const inApp = /FBAN|FBAV|Instagram|Line\/|Teams|WhatsApp/i.test(ua);
    browser = !wrapper && !inApp && /Safari/i.test(ua) ? 'safari' : 'ios-other';
  } else {
    browser = /Chrome|Chromium|Edg\//i.test(ua) ? 'chromium' : 'other';
  }

  const installed = env.iosStandalone === true || env.displayModeStandalone === true;

  return {
    ios,
    android,
    browser,
    installed,
    canInstall: ios ? browser === 'safari' : env.hasInstallPrompt === true || browser === 'chromium',
    // Background Sync is Chromium-only in practice; trust the feature check
    // where it exists and never assume it on iOS.
    backgroundSync: !ios && env.hasBackgroundSync === true,
  };
}

export interface InstallAdvice {
  show: boolean;
  /** How the engineer installs it, in the words on their screen. */
  steps: string[];
  headline: string;
  /** Set when the browser they are in cannot install, whatever they tap. */
  wrongBrowser: boolean;
}

/**
 * What to tell the engineer about getting the app onto their home screen.
 *
 * Installing is not cosmetic here. An installed PWA gets a larger storage quota
 * and is evicted less aggressively, which on this app is the difference between
 * a day's photos surviving a night with no signal and not.
 */
export function installAdvice(p: Platform): InstallAdvice {
  if (p.installed) {
    return { show: false, headline: '', steps: [], wrongBrowser: false };
  }
  if (p.ios && p.browser === 'safari') {
    return {
      show: true,
      wrongBrowser: false,
      headline: 'Add this to your home screen before you start',
      steps: [
        'Tap the Share button at the bottom of Safari.',
        'Scroll down and tap "Add to Home Screen".',
        'Tap Add, then open Cx Capture from the home screen icon.',
      ],
    };
  }
  if (p.ios) {
    return {
      show: true,
      wrongBrowser: true,
      headline: 'Open this in Safari to install it',
      steps: [
        'Only Safari can add an app to the iPhone home screen.',
        'Copy this page’s address, open Safari and paste it there.',
        'Then use Share → "Add to Home Screen".',
      ],
    };
  }
  if (p.canInstall) {
    return {
      show: true,
      wrongBrowser: false,
      headline: 'Install this for offline capture',
      steps: ['Use the install button in the address bar, or the browser menu → Install app.'],
    };
  }
  return { show: false, headline: '', steps: [], wrongBrowser: false };
}

/**
 * What the app may honestly promise about uploads.
 *
 * Saying "we'll finish in the background" when iOS will not is the kind of lie
 * that costs a site visit, so the wording is derived from the capability rather
 * than written once and hoped for.
 */
export function syncPromise(p: Platform): string {
  return p.backgroundSync
    ? 'Uploads continue in the background once you have signal.'
    : 'Keep this app open and on screen while photos upload — they stop if you switch away.';
}

/** Reads the real browser. Kept to one place so nothing else touches `navigator`. */
export function currentEnv(): Env {
  const nav = navigator as Navigator & { standalone?: boolean };
  return {
    userAgent: nav.userAgent,
    iosStandalone: nav.standalone,
    displayModeStandalone:
      typeof matchMedia === 'function' ? matchMedia('(display-mode: standalone)').matches : undefined,
    hasInstallPrompt: 'onbeforeinstallprompt' in globalThis,
    hasBackgroundSync: 'ServiceWorkerRegistration' in globalThis && 'sync' in ServiceWorkerRegistration.prototype,
  };
}
